import { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, FileTextIcon, PlusIcon } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { useRole } from '@/hooks/use-role';
import {
  businessQuotesQueryKey,
  createJobQuote,
  listBusinessQuotes,
  type ListBusinessQuotesFilters,
} from '@/lib/api/quotes';

import { AddQuoteModal } from './add-quote-modal';
import { PAGE_SIZE } from './helpers';
import { QuoteDetailModal } from './quote-detail-modal';
import { QuotesGrid } from './quotes-grid';
import type { NewQuoteSubmission, Quote, QuoteStatusFilter } from './types';

const STATUS_TABS: QuoteStatusFilter[] = ['all', 'pending', 'accepted', 'declined'];

export function Quotes() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const { isOwner } = useRole();

  const [status, setStatus] = useState<QuoteStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [active, setActive] = useState<Quote | null>(null);
  const [autoShareOnOpen, setAutoShareOnOpen] = useState(false);

  const filters = useMemo<ListBusinessQuotesFilters>(
    () => (status === 'all' ? {} : { status }),
    [status],
  );

  const quotesQuery = useQuery({
    queryKey: businessQuotesQueryKey(businessId ?? '', filters),
    queryFn: () => listBusinessQuotes(businessId!, filters),
    enabled: Boolean(businessId) && isOwner,
  });

  const createMutation = useMutation({
    mutationFn: (s: NewQuoteSubmission) =>
      createJobQuote(businessId!, s.jobId, {
        total_amount: s.total_amount,
        line_items: s.line_items,
        notes: s.notes,
      }),
    meta: { successMessage: t.quoteCreated },
    onSuccess: (quote) => {
      setIsAdding(false);
      setAutoShareOnOpen(true);
      setActive(quote);
    },
  });

  const quotes = quotesQuery.data ?? [];
  const totalPages = Math.max(1, Math.ceil(quotes.length / PAGE_SIZE));
  const paginated = quotes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleStatus(next: QuoteStatusFilter) {
    setStatus(next);
    setPage(1);
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <FileTextIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">{t.quotesTitle}</h1>
            <p className="text-xs text-muted-foreground">
              {quotes.length} {t.quotesCount}
            </p>
          </div>
        </div>
        {isOwner && (
          <Button
            size="sm"
            className="gap-1.5"
            data-testid="add-quote"
            onClick={() => setIsAdding(true)}
          >
            <PlusIcon className="w-4 h-4" />
            {t.addQuote}
          </Button>
        )}
      </div>

      {!isOwner ? (
        <p className="text-sm text-muted-foreground py-10 text-center">
          {t.quotesOwnerOnly}
        </p>
      ) : (
        <>
          <div className="flex gap-1 border-b mb-5">
            {STATUS_TABS.map((s) => (
              <button
                key={s}
                onClick={() => handleStatus(s)}
                className={[
                  'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  status === s
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                ].join(' ')}
                data-testid={`filter-status-${s}`}
              >
                {s === 'all'
                  ? t.allStatuses
                  : s === 'pending'
                    ? t.quoteStatusPending
                    : s === 'accepted'
                      ? t.quoteStatusAccepted
                      : t.quoteStatusDeclined}
              </button>
            ))}
          </div>

          {quotesQuery.isPending ? (
            <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
          ) : (
            <QuotesGrid
              quotes={paginated}
              onSelect={(quote) => {
                setAutoShareOnOpen(false);
                setActive(quote);
              }}
            />
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="gap-1.5"
                data-testid="prev-page"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                {t.previous}
              </Button>
              <span className="text-xs text-muted-foreground">
                {page} {t.pageOf} {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="gap-1.5"
                data-testid="next-page"
              >
                {t.nextPage}
                <ChevronRightIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {isAdding && businessId && (
        <AddQuoteModal
          businessId={businessId}
          onClose={() => setIsAdding(false)}
          onSave={(s) => createMutation.mutate(s)}
          isSaving={createMutation.isPending}
        />
      )}

      {active && businessId && (
        <QuoteDetailModal
          businessId={businessId}
          jobId={active.job_id}
          quoteId={active.id}
          canApprove={isOwner}
          canManage={isOwner}
          autoShareLink={autoShareOnOpen}
          onClose={() => {
            setActive(null);
            setAutoShareOnOpen(false);
          }}
        />
      )}
    </div>
  );
}
